import { useCallback, useRef, useState } from "react";
import {
  createScheduleGroup,
  deleteScheduleGroup,
  patchScheduleGroup,
} from "../../../../../services/api/schedule";
import { mapScheduleGroupFromApi } from "../../model/scheduleApiMappers";

/**
 * Manage schedule-group create modal state plus inline group renames and deletes.
 *
 * @param {Object} params
 * @param {Array<Object>} params.groups Current schedule groups.
 * @param {Function} params.setGroups
 * @param {Function} params.setBlocks Reassign blocks when a group is removed.
 * @param {Function} params.setExpandedProjectIds
 * @param {Function} params.setExpandedGroupIds
 * @param {Function} params.runPlanningOperation PlanningContext mutation wrapper.
 * @returns {{
 *   createGroupModalOpen: boolean,
 *   openCreateGroup: (projectId: string|number, parentGroupId?: string|number|null) => void,
 *   handleCloseCreateGroupModal: () => void,
 *   handleSaveScheduleGroup: (payload: { name: string }) => Promise<void>,
 *   renameScheduleGroup: (groupId: string|number, name: string) => Promise<void>,
 *   removeScheduleGroup: (groupId: string|number) => Promise<void>,
 * }}
 */
export function useScheduleGroupActions({
  groups,
  setGroups,
  setBlocks,
  setExpandedProjectIds,
  setExpandedGroupIds,
  runPlanningOperation,
}) {
  const [createGroupModalOpen, setCreateGroupModalOpen] = useState(false);
  const createGroupIntentRef = useRef(null);

  /** Open create-group modal scoped to a project (and optional parent group). */
  const openCreateGroup = useCallback((projectId, parentGroupId = null) => {
    createGroupIntentRef.current = { projectId, parentGroupId };
    setCreateGroupModalOpen(true);
  }, []);

  /** Close group modal and reset pending create intent state. */
  const handleCloseCreateGroupModal = useCallback(() => {
    setCreateGroupModalOpen(false);
    createGroupIntentRef.current = null;
  }, []);

  /** Create a new group at the appropriate sibling sort position. */
  const handleSaveScheduleGroup = useCallback(
    async ({ name }) => {
      const intent = createGroupIntentRef.current;
      if (!intent) {
        return;
      }
      const { projectId, parentGroupId } = intent;
      const siblings = groups.filter(
        (g) =>
          g.projectId === projectId &&
          (g.parentGroupId ?? null) === (parentGroupId ?? null)
      );
      const maxOrder =
        siblings.length > 0
          ? Math.max(...siblings.map((g) => g.order ?? 0))
          : 0;
      handleCloseCreateGroupModal();
      try {
        const raw = await runPlanningOperation({
          module: "schedule",
          operation: "createScheduleGroup",
          resources: ["schedule.groups"],
          apiCall: () =>
            createScheduleGroup({
              job_id: projectId,
              parent_id: parentGroupId ?? null,
              name: name.trim(),
              color: "",
              sort_order: maxOrder + 1,
            }),
        });
        setGroups((prev) => [...prev, mapScheduleGroupFromApi(raw)]);
        setExpandedProjectIds((prev) => {
          const next = new Set(prev);
          next.add(projectId);
          return next;
        });
        if (parentGroupId) {
          setExpandedGroupIds((prev) => {
            const next = new Set(prev);
            next.add(parentGroupId);
            return next;
          });
        }
      } catch (e) {
        console.error("Schedule group create failed", e);
      }
    },
    [
      groups,
      handleCloseCreateGroupModal,
      setExpandedGroupIds,
      setExpandedProjectIds,
      setGroups,
      runPlanningOperation,
    ]
  );

  /** Persist an inline group name edit without touching color or hierarchy fields. */
  const renameScheduleGroup = useCallback(
    async (groupId, name) => {
      const trimmedName = name.trim();
      const existing = groups.find((g) => String(g.id) === String(groupId));
      if (!existing || !trimmedName || trimmedName === existing.name) {
        return;
      }
      try {
        const raw = await runPlanningOperation({
          module: "schedule",
          operation: "patchScheduleGroup",
          resources: ["schedule.groups"],
          apiCall: () =>
            patchScheduleGroup(groupId, {
              name: trimmedName,
            }),
        });
        const mapped = mapScheduleGroupFromApi(raw);
        if (mapped) {
          setGroups((prev) =>
            prev.map((g) => (String(g.id) === String(groupId) ? mapped : g))
          );
        }
      } catch (e) {
        console.error("Schedule group rename failed", e);
      }
    },
    [groups, runPlanningOperation, setGroups]
  );

  /** Delete a group/subgroup and move any affected blocks back to their project row locally. */
  const removeScheduleGroup = useCallback(
    async (groupId) => {
      const collectDeletedIds = (rootId) => {
        const ids = new Set([String(rootId)]);
        let changed = true;
        while (changed) {
          changed = false;
          for (const group of groups) {
            if (
              group.parentGroupId != null &&
              ids.has(String(group.parentGroupId)) &&
              !ids.has(String(group.id))
            ) {
              ids.add(String(group.id));
              changed = true;
            }
          }
        }
        return ids;
      };
      const deletedIds = collectDeletedIds(groupId);
      try {
        await runPlanningOperation({
          module: "schedule",
          operation: "deleteScheduleGroup",
          resources: ["schedule.groups", "schedule.blocks"],
          entityIds: [...deletedIds],
          apiCall: () => deleteScheduleGroup(groupId),
        });
        setGroups((prev) =>
          prev.filter((group) => !deletedIds.has(String(group.id)))
        );
        setExpandedGroupIds((prev) => {
          const next = new Set(prev);
          for (const id of deletedIds) {
            next.delete(id);
          }
          return next;
        });
        setBlocks((prev) =>
          prev.map((block) =>
            block.scheduleGroup?.id != null &&
              deletedIds.has(String(block.scheduleGroup.id))
              ? { ...block, scheduleGroup: null }
              : block
          )
        );
      } catch (e) {
        console.error("Schedule group delete failed", e);
      }
    },
    [groups, runPlanningOperation, setBlocks, setExpandedGroupIds, setGroups]
  );

  return {
    createGroupModalOpen,
    openCreateGroup,
    handleCloseCreateGroupModal,
    handleSaveScheduleGroup,
    renameScheduleGroup,
    removeScheduleGroup,
  };
}

